// 同じ座標に重なるピン（1つの分譲地の複数区画など）を、少しずつずらして全部押せるようにする。
//
// 地図のピンは集約せず1物件1ピンで出す。そのままだと同座標のピンは完全に重なり、
// 一番上の1本しか押せない。そこで同座標のグループだけ、真の位置を中心とした小さな円周上に並べる。
//
// ずらす量は「画面上の px」で一定にする。緯度経度で固定するとズームアウトで重なり、
// ズームインで分譲地から大きく離れてしまうため。マーカーは idle のたびに作り直されるので、
// 作成時点のズームで計算すればズーム後も自然に追随する。
//
// Google Maps には依存させない（単体テストできるようにするため）。

// Web Mercator のズーム0・赤道での 1px あたりのメートル数（256px タイル基準）
export const EQUATOR_METERS_PER_PIXEL = 156543.03392;

// 地球半径（Web Mercator の球体近似）。緯度1度あたりの距離の換算に使う
const EARTH_RADIUS_METERS = 6378137;
const METERS_PER_DEGREE = (EARTH_RADIUS_METERS * Math.PI) / 180;

// 同座標とみなす桁数。7桁 ≒ 1cm で、API の座標の表記ゆれ（末尾0の有無など）だけを吸収する
export const COORDINATE_PRECISION = 7;

// ピンの中心どうしを最低これだけ離す（px）。
// ピンの外枠は 40px（35px + margin 2.5px、選択時は 40px そのもの）なので、4px の隙間を足して 44px。
// アンカー（下端中央）を動かすとピン全体が平行移動するだけなので、アンカー間の距離＝ピン間の距離になる
export const PIN_SPACING_PX = 44;

// 2件のときでも左右に離れて見えるよう、円の半径はこれより小さくしない（px）
export const MIN_SPREAD_RADIUS_PX = PIN_SPACING_PX / 2;

// 1重の円で並べる上限。これを超えると半径が大きくなりすぎて分譲地から離れて見えるので、
// 同心円（内側から順に詰める）に切り替える。8件で半径は約 58px
export const MAX_SINGLE_RING_COUNT = 8;

/** ズームと緯度から、画面上 1px が地上の何メートルにあたるか */
export const metersPerPixel = (lat, zoom) =>
    (EQUATOR_METERS_PER_PIXEL * Math.cos((lat * Math.PI) / 180)) /
    Math.pow(2, zoom);

/** 同座標判定用のキー。桁を固定して文字列にする */
export const coordinateKey = (lat, lng) =>
    `${Number(lat).toFixed(COORDINATE_PRECISION)},${Number(lng).toFixed(COORDINATE_PRECISION)}`;

// 並び順を固定するための比較。ID は数値の文字列で来るので数値として比べ、
// 数値でないものだけ文字列で比べる（"10" が "9" より前に来ないように）
const compareIds = (a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    const sa = String(a ?? "");
    const sb = String(b ?? "");
    if (sa === sb) return 0;
    return sa < sb ? -1 : 1;
};

// count 件を半径 radius の円周に等間隔で置く。
// 西（左）から時計回りに回すので、2件なら左右、3件以上は左から上を通って並ぶ
const placeOnRing = (count, radius, out) => {
    for (let i = 0; i < count; i++) {
        const angle = Math.PI - (2 * Math.PI * i) / count;
        out.push({ x: radius * Math.cos(angle), y: radius * Math.sin(angle) });
    }
};

/**
 * count 件を重ならないように並べるときの、真の位置からのずれ（px）を返す。
 * x は東向き、y は北向き（画面の上向き）が正。1件なら [{x:0,y:0}]。
 */
export const spreadOffsetsPx = (count) => {
    if (count <= 1) return [{ x: 0, y: 0 }];

    const offsets = [];

    if (count <= MAX_SINGLE_RING_COUNT) {
        // 隣どうしの弦の長さ 2R·sin(π/n) が間隔以上になる最小の半径
        const radius = Math.max(
            MIN_SPREAD_RADIUS_PX,
            PIN_SPACING_PX / (2 * Math.sin(Math.PI / count))
        );
        placeOnRing(count, radius, offsets);
        return offsets;
    }

    // 件数が多いときは同心円。内側の円から定員まで詰め、最後の円は残りを等分する。
    // 定員は「隣どうしの弦が間隔以上」になる最大件数（円周 ÷ 間隔だと弦が足りず重なる）。
    // 円どうしも間隔ぶん離すので、別の円のピンとも重ならない
    let remaining = count;
    let radius = MIN_SPREAD_RADIUS_PX;
    while (remaining > 0) {
        const capacity = Math.max(
            1,
            Math.floor(
                Math.PI / Math.asin(Math.min(1, PIN_SPACING_PX / (2 * radius)))
            )
        );
        const n = Math.min(capacity, remaining);
        placeOnRing(n, radius, offsets);
        remaining -= n;
        radius += PIN_SPACING_PX;
    }
    return offsets;
};

/** 真の位置から px だけずらした緯度経度。ずれが小さいので平面近似で十分 */
export const offsetLatLng = (lat, lng, offsetPx, zoom) => {
    const mpp = metersPerPixel(lat, zoom);
    const cosLat = Math.cos((lat * Math.PI) / 180);
    return {
        lat: lat + (offsetPx.y * mpp) / METERS_PER_DEGREE,
        lng: lng + (offsetPx.x * mpp) / (METERS_PER_DEGREE * cosLat),
    };
};

/**
 * 物件ごとの表示位置を決める。
 * 戻り値は { property, position: {lat, lng}, spread } の配列。
 * 同座標が1件だけの物件は真の位置のまま（spread=false）、2件以上はずらした位置（spread=true）。
 * 同座標グループの中は物件IDの昇順で並べ、再描画のたびに同じ物件が同じ場所へ出るようにする。
 *
 * @param {Array<{id: (string|number), geopoint: {lat, lng}}>} properties geopoint を持つ物件
 * @param {number} zoom 現在のズーム（小数可）
 */
export const layoutPins = (properties, zoom) => {
    const groups = new Map();
    (properties ?? []).forEach((property) => {
        const lat = Number(property.geopoint.lat);
        const lng = Number(property.geopoint.lng);
        const key = coordinateKey(lat, lng);
        let group = groups.get(key);
        if (!group) {
            group = { lat, lng, members: [] };
            groups.set(key, group);
        }
        group.members.push(property);
    });

    const result = [];
    groups.forEach(({ lat, lng, members }) => {
        if (members.length === 1) {
            result.push({ property: members[0], position: { lat, lng }, spread: false });
            return;
        }

        const sorted = [...members].sort((a, b) => compareIds(a.id, b.id));
        const offsets = spreadOffsetsPx(sorted.length);
        sorted.forEach((property, i) => {
            result.push({
                property,
                position: offsetLatLng(lat, lng, offsets[i], zoom),
                spread: true,
            });
        });
    });
    return result;
};
